import * as THREE from "three";
import { SNAPSHOT_BUFFER_MS, type PlayerState } from "@mmo/shared";
import { Character } from "./character";
import { lookupModel } from "./model-registry";

type SnapshotEntry = {
  serverTime: number;
  players: Map<string, PlayerState>;
};

export class RemotePlayers {
  private scene: THREE.Scene;
  private buffer: SnapshotEntry[] = [];
  private chars = new Map<string, Character>();
  private knownNames = new Map<string, string>();
  private localId = "";
  private serverTimeOffset = 0;
  private offsetInitialized = false;

  constructor(scene: THREE.Scene) { this.scene = scene; }

  setLocalId(id: string) { this.localId = id; }

  ingest(serverTime: number, players: PlayerState[]) {
    const map = new Map<string, PlayerState>();
    for (const p of players) {
      map.set(p.id, p);
      this.knownNames.set(p.id, p.name);
    }
    this.buffer.push({ serverTime, players: map });
    if (this.buffer.length > 30) this.buffer.shift();

    const sampleOffset = serverTime - performance.now();
    if (!this.offsetInitialized) {
      this.serverTimeOffset = sampleOffset;
      this.offsetInitialized = true;
    } else {
      this.serverTimeOffset += (sampleOffset - this.serverTimeOffset) * 0.05;
    }
  }

  onShot(shooterId: string, kind: "primary" | "melee") {
    if (shooterId === this.localId) return;
    const c = this.chars.get(shooterId);
    if (!c) return;
    if (kind === "melee") c.triggerMelee();
    else c.triggerFire();
  }

  render(dt: number) {
    for (const c of this.chars.values()) c.update(dt);

    if (this.buffer.length < 2) return;
    const renderTime = performance.now() + this.serverTimeOffset - SNAPSHOT_BUFFER_MS;

    let a: SnapshotEntry | null = null;
    let b: SnapshotEntry | null = null;
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      if (this.buffer[i].serverTime <= renderTime) {
        a = this.buffer[i];
        b = this.buffer[i + 1] ?? null;
        break;
      }
    }
    if (!a) a = this.buffer[0];
    if (!b) b = this.buffer[this.buffer.length - 1];

    const span = b.serverTime - a.serverTime;
    const t = span > 0 ? Math.max(0, Math.min(1, (renderTime - a.serverTime) / span)) : 0;

    const seen = new Set<string>();
    for (const [id, pa] of a.players) {
      if (id === this.localId) continue;
      const pb = b.players.get(id) ?? pa;
      seen.add(id);

      const x = pa.position.x + (pb.position.x - pa.position.x) * t;
      const y = pa.position.y + (pb.position.y - pa.position.y) * t;
      const z = pa.position.z + (pb.position.z - pa.position.z) * t;
      const yaw = shortestAngleLerp(pa.yaw, pb.yaw, t);

      const char = this.ensureChar(id, pa.name, pa.classId);
      char.setClassId(pa.classId);
      char.setPose({ x, y, z }, yaw, pa.velocity.x, pa.velocity.z, pa.onGround, pa.dodgingTimer);
    }

    for (const [id, c] of this.chars) {
      if (!seen.has(id)) {
        c.dispose();
        this.chars.delete(id);
      }
    }
  }

  private ensureChar(id: string, name: string, classId: string): Character {
    const existing = this.chars.get(id);
    if (existing) return existing;
    const entry = lookupModel(name);
    const c = new Character(this.scene, id, classId, entry);
    this.chars.set(id, c);
    return c;
  }
}

function shortestAngleLerp(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}
